/**
 * Shared HTTP contract and fetch wrapper for frontend API calls.
 *
 * - Attaches auth and extra headers for backend and Graph requests
 * - Normalizes error shape for all consumers
 * - Supports JSON, FormData, and streaming
 * - Replaces Axios for all new and migrated code
 */

export interface HttpRequestOptions extends RequestInit {
  /**
   * If true, attaches backend auth and Extra-Attributes headers
   */
  withBackendAuth?: boolean;
  /**
   * If true, attaches Graph API auth header
   */
  withGraphAuth?: boolean;
  /**
   * Optional: treat non-2xx as error (default true)
   */
  throwOnError?: boolean;
}

export interface HttpError extends Error {
  status: number;
  data?: any;
  headers?: Headers;
}

/**
 * Resolves backend API URLs the same way the legacy Axios client did.
 *
 * For backend-authenticated calls, relative paths are prefixed with
 * `VITE_BASE_URL` when configured, so requests are sent to the backend host
 * instead of the frontend dev server origin.
 */
const resolveRequestUrl = (url: string, withBackendAuth?: boolean): string => {
  if (!withBackendAuth) {
    return url;
  }

  if (/^https?:\/\//i.test(url)) {
    return url;
  }

  const baseUrl = import.meta.env.VITE_BASE_URL as string | undefined;
  if (!baseUrl) {
    return url;
  }

  const normalizedBaseUrl = baseUrl.endsWith('/')
    ? baseUrl.slice(0, -1)
    : baseUrl;
  const normalizedPath = url.startsWith('/') ? url : `/${url}`;

  return `${normalizedBaseUrl}${normalizedPath}`;
};

export async function httpFetch(
  url: string,
  options: HttpRequestOptions = {}
): Promise<Response> {
  const {
    withBackendAuth,
    withGraphAuth,
    throwOnError = true,
    ...rest
  } = options;
  const headers = new Headers(rest.headers || {});

  // Attach auth headers as needed
  if (withBackendAuth) {
    const { getAccessTokenFor } = await import('../../msal/msalService');
    const { apiRequest } = await import('../../msal/msalConfig');
    const { getEncodedExtraAttributes } =
      await import('../../utils/sessionStorageUtils');
    const token = await getAccessTokenFor(apiRequest);
    if (!token) {
      throw new Error('Access token is not available');
    }
    headers.set('Authorization', `Bearer ${token}`);
    headers.set(
      'Extra-Attributes',
      JSON.stringify(getEncodedExtraAttributes())
    );
  }
  if (withGraphAuth) {
    const { getAccessTokenFor } = await import('../../msal/msalService');
    const { graphRequest } = await import('../../msal/msalConfig');
    const token = await getAccessTokenFor(graphRequest);
    if (!token) {
      throw new Error('Access token is not available for Graph API');
    }
    headers.set('Authorization', `Bearer ${token}`);
  }

  const requestUrl = resolveRequestUrl(url, withBackendAuth);
  const response = await fetch(requestUrl, { ...rest, headers });

  if (throwOnError && !response.ok) {
    let data: any = undefined;
    try {
      data = await response.clone().json();
    } catch {
      // ignore
    }
    const error: HttpError = Object.assign(
      new Error(`HTTP ${response.status}: ${response.statusText}`),
      {
        status: response.status,
        data,
        headers: response.headers,
      }
    );
    throw error;
  }

  return response;
}

/**
 * Helper for JSON requests (auto-parses response)
 */
export async function httpFetchJson<T = any>(
  url: string,
  options: HttpRequestOptions = {}
): Promise<T> {
  const response = await httpFetch(url, options);
  return response.json();
}

/**
 * Helper for streaming (returns Response for caller to handle)
 */
export async function httpFetchStream(
  url: string,
  options: HttpRequestOptions = {}
): Promise<Response> {
  return httpFetch(url, options);
}
